import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Check, X } from 'lucide-react';

const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSelectPlan = (tier: string) => {
    if (!user) {
      navigate('/register');
    } else if (tier !== 'free') {
      alert('Stripe決済は近日対応予定です！現在はすべての機能を無料でお使いいただけます。');
    }
  };

  const plans = [
    {
      name: '無料プラン',
      price: '¥0',
      tier: 'free',
      features: [
        { name: '月間5回まで生成可能', included: true },
        { name: '基本的なコンテンツタイプ', included: true },
        { name: 'メールサポート', included: true },
        { name: '生成履歴の保存', included: false },
        { name: '優先サポート', included: false },
        { name: 'APIアクセス', included: false },
      ],
      buttonText: '現在のプラン',
      buttonStyle: 'bg-gray-200 text-gray-700 cursor-default',
    },
    {
      name: 'ベーシック',
      price: '¥1,000',
      tier: 'basic',
      features: [
        { name: '月間100回まで生成可能', included: true },
        { name: 'すべてのコンテンツタイプ', included: true },
        { name: 'メールサポート', included: true },
        { name: '生成履歴の保存', included: true },
        { name: '優先サポート', included: true },
        { name: 'APIアクセス', included: false },
      ],
      buttonText: '無料で試す',
      buttonStyle: 'bg-blue-600 text-white hover:bg-blue-700',
      recommended: true,
    },
    {
      name: 'プロ',
      price: '¥3,000',
      tier: 'pro',
      features: [
        { name: '無制限に生成可能', included: true },
        { name: 'すべてのコンテンツタイプ', included: true },
        { name: 'メールサポート', included: true },
        { name: '生成履歴の保存', included: true },
        { name: '24時間365日優先サポート', included: true },
        { name: 'APIアクセス', included: true },
      ],
      buttonText: 'お問い合わせ',
      buttonStyle: 'bg-gray-800 text-white hover:bg-gray-900',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">シンプルでわかりやすい料金プラン</h1>
        <p className="text-xl text-gray-600">あなたのニーズに合ったプランをお選びください</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.tier}
            className={`bg-white rounded-lg shadow-lg overflow-hidden ${
              plan.recommended ? 'ring-2 ring-blue-600' : ''
            }`}
          >
            {plan.recommended && (
              <div className="bg-blue-600 text-white text-center py-2 text-sm font-semibold">
                おすすめ
              </div>
            )}
            <div className="p-8">
              <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className="text-gray-600">/月</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    {feature.included ? (
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    ) : (
                      <X className="h-5 w-5 text-gray-300 mr-2 flex-shrink-0" />
                    )}
                    <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSelectPlan(plan.tier)}
                className={`w-full py-3 px-4 rounded-md font-semibold ${plan.buttonStyle} ${
                  user?.subscriptionTier === plan.tier ? 'cursor-default' : ''
                }`}
                disabled={user?.subscriptionTier === plan.tier}
              >
                {user?.subscriptionTier === plan.tier ? '現在のプラン' : plan.buttonText}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto text-left space-y-6">
          <div>
            <h3 className="font-semibold mb-2">Can I change plans anytime?</h3>
            <p className="text-gray-600">
              Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
            <p className="text-gray-600">
              We accept all major credit cards through our secure payment processor, Stripe.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Is there a free trial?</h3>
            <p className="text-gray-600">
              Yes! All paid plans come with a 7-day free trial. No credit card required to start.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;